import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/lib/api/client';
import { useAuthStore } from '@/stores/auth-store';

const schema = z.object({
  email: z.string().min(1, 'Email is required').email('That does not look like an email address'),
  password: z.string().min(1, 'Password is required'),
  // Only sent when the account has 2FA switched on; the API asks for it by
  // rejecting the first attempt.
  totp: z.string().optional(),
  remember: z.boolean(),
});

type LoginValues = z.infer<typeof schema>;

export function LoginRoute() {
  const login = useAuthStore((s) => s.login);
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [needsTotp, setNeedsTotp] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm<LoginValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '', totp: '', remember: true },
  });

  const from = (location.state as { from?: string } | null)?.from;

  const onSubmit = async (values: LoginValues) => {
    setFormError(null);
    try {
      await login({
        email: values.email.trim(),
        password: values.password,
        totp: values.totp?.trim() || undefined,
        remember: values.remember,
      });
      navigate(from && from !== '/login' ? from : '/dashboard', { replace: true });
    } catch (error) {
      if (error instanceof ApiError && /two-factor/i.test(error.message)) {
        setNeedsTotp(true);
        setFormError('Enter the six-digit code from your authenticator app.');
        return;
      }
      setFormError(
        error instanceof ApiError && error.status === 403
          ? 'This account has been deactivated. Contact your fleet administrator.'
          : 'Those credentials were not accepted. Check your email and password.',
      );
    }
  };

  const submitting = form.formState.isSubmitting;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.2, 0.8, 0.2, 1] }}
      className="w-full max-w-[380px] space-y-7"
    >
      <header className="space-y-1.5">
        <p className="label-micro">Operations console</p>
        <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
        <p className="text-sm text-muted-foreground">
          Use the credentials issued by your fleet administrator.
        </p>
      </header>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="label-micro">Email</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="email"
                    autoComplete="email"
                    autoFocus
                    placeholder="you@fleet.com"
                    className="h-10"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel className="label-micro">Password</FormLabel>
                  <Link
                    to="/forgot-password"
                    className="text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
                <FormControl>
                  <div className="relative">
                    <Input
                      {...field}
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="••••••••••"
                      className="h-10 pr-9"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((value) => !value)}
                      className="absolute top-1/2 right-2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground transition-colors hover:text-foreground"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {needsTotp && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
              <FormField
                control={form.control}
                name="totp"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="label-micro">Authentication code</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        placeholder="000000"
                        className="tnum h-10 tracking-[0.4em]"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </motion.div>
          )}

          <FormField
            control={form.control}
            name="remember"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center gap-2 space-y-0">
                <FormControl>
                  <Checkbox checked={field.value} onCheckedChange={field.onChange} id="remember" />
                </FormControl>
                <FormLabel htmlFor="remember" className="text-xs font-normal text-muted-foreground">
                  Keep me signed in on this device
                </FormLabel>
              </FormItem>
            )}
          />

          {formError && (
            <motion.p
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              role="alert"
              className="rounded-sm border border-destructive/30 bg-destructive/8 px-3 py-2 text-xs text-destructive"
            >
              {formError}
            </motion.p>
          )}

          {/* Fixed width across states so the button never resizes mid-submit. */}
          <Button type="submit" disabled={submitting} className="h-10 w-full">
            {submitting ? <Loader2 className="size-4 animate-spin" /> : 'Sign in'}
          </Button>
        </form>
      </Form>

      <p className="text-[11px] leading-relaxed text-muted-foreground">
        Access is scoped to your company. Every sign-in attempt, successful or not, is written to
        the audit log.
      </p>
    </motion.div>
  );
}
